/**
 * Starea locală a emailului de verificare: cooldown-ul de resend și ultimul
 * email pe care l-am trimis din acest browser.
 *
 * Cooldown-ul de 5 min oglindește backstop-ul din backend
 * (emailVerification.ts → VERIFICATION_RESEND_COOLDOWN_MS). Timer-ul e persistat
 * în localStorage, cheiat pe email, ca un reload al paginii să NU reseteze
 * numărătoarea - altfel butonul „5 min" s-ar ocoli cu un simplu refresh.
 *
 * Aceleași chei servesc și la precompletarea emailului după verificare: linkul
 * din email aduce omul pe `/?verified=1`, fără niciun email în URL, iar singurul
 * loc unde browserul îl mai știe e marcajul lăsat la register / login blocat.
 */

export const RESEND_COOLDOWN_S = 5 * 60;
export const RESEND_TS_PREFIX = "amwai_verify_resent_at:";

export function resendKey(email: string): string {
  return `${RESEND_TS_PREFIX}${email.trim().toLowerCase()}`;
}

/** Câte secunde au mai rămas din cooldown pentru acest email (0 = gata). */
export function resendCooldownLeft(email: string): number {
  try {
    const at = Number(localStorage.getItem(resendKey(email)));
    if (!at) return 0;
    return Math.max(0, RESEND_COOLDOWN_S - Math.floor((Date.now() - at) / 1000));
  } catch {
    return 0;
  }
}

/** Marchează „tocmai am trimis" - pornește cooldown-ul (și la signup, și la resend). */
export function markVerificationSent(email: string): void {
  try {
    localStorage.setItem(resendKey(email), String(Date.now()));
  } catch {
    /* private mode / storage blocked - timer-ul devine best-effort */
  }
}

/**
 * Emailul cu cel mai recent marcaj din acest browser (null = niciunul).
 * Cu `consume`, marcajele se șterg: verificarea s-a încheiat, cooldown-ul de
 * resend nu mai are ce păzi, iar emailul nu trebuie să rămână în storage.
 */
export function lastVerificationEmail(consume = false): string | null {
  try {
    let best: { email: string; at: number } | null = null;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(RESEND_TS_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      const at = Number(localStorage.getItem(k));
      if (at && (!best || at > best.at)) best = { email: k.slice(RESEND_TS_PREFIX.length), at };
    }
    if (consume) keys.forEach((k) => localStorage.removeItem(k));
    return best?.email ?? null;
  } catch {
    return null;
  }
}
